// src/features/team/NatureLevelEditor.tsx

import { X } from 'lucide-react';
import type { StatKey } from '../../core/types';
import { MOCK_NATURES } from '../../data/mock-natures';

const STAT_LABELS: Record<StatKey, string> = {
  hp: '生命',
  atk: '物攻',
  spa: '魔攻',
  def: '物防',
  spd: '魔防',
  spe: '速度',
};

/** 分组顺序与标题 */
const GROUP_ORDER: { key: StatKey | 'neutral'; label: string }[] = [
  { key: 'hp', label: '生命性格' },
  { key: 'atk', label: '物攻性格' },
  { key: 'spa', label: '魔攻性格' },
  { key: 'spe', label: '速度性格' },
  { key: 'def', label: '物防性格' },
  { key: 'spd', label: '魔防性格' },
  { key: 'neutral', label: '中性性格' },
];

interface NatureLevelEditorProps {
  open: boolean;
  onClose: () => void;
  spiritName?: string;
  natureId: string;
  onChangeNature: (id: string) => void;
}

export function NatureLevelEditor({
  open,
  onClose,
  spiritName,
  natureId,
  onChangeNature,
}: NatureLevelEditorProps) {
  if (!open) return null;

  // 按增益属性分组
  const grouped = GROUP_ORDER.map(({ key, label }) => ({
    key,
    label,
    natures: MOCK_NATURES.filter((n) =>
      key === 'neutral' ? !n.plus && !n.minus : n.plus === key,
    ),
  })).filter((g) => g.natures.length > 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-white">选择性格</h2>
            {spiritName && (
              <p className="text-xs text-slate-400 mt-0.5">{spiritName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition"
            aria-label="关闭"
          >
            <X size={20} />
          </button>
        </div>

        {/* 性格分组列表 */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {grouped.map((group) => (
            <div key={group.key}>
              {/* 分组标题 */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-primary-400">
                  {group.label}
                </span>
                <div className="flex-1 h-px bg-slate-700" />
              </div>

              {/* 该组的性格卡片 */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {group.natures.map((nature) => (
                  <NatureCard
                    key={nature.id}
                    nature={nature}
                    selected={natureId === nature.id}
                    onClick={() => onChangeNature(nature.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* 底部 */}
        <div className="px-6 py-3 border-t border-slate-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium transition"
          >
            完成
          </button>
        </div>
      </div>
    </div>
  );
}

/** 单个性格卡片 */
function NatureCard({
  nature,
  selected,
  onClick,
}: {
  nature: (typeof MOCK_NATURES)[number];
  selected: boolean;
  onClick: () => void;
}) {
  const hasEffect = Boolean(nature.plus || nature.minus);

  return (
    <button
      onClick={onClick}
      className={`text-left p-2.5 rounded-xl border transition ${
        selected
          ? 'border-primary-500 bg-primary-500/10'
          : 'border-slate-700 bg-slate-900/50 hover:border-slate-500 hover:bg-slate-900'
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-white">
          {nature.name}
        </span>
        {!hasEffect && (
          <span className="text-[10px] text-slate-500">无修正</span>
        )}
      </div>
      {hasEffect && (
        <div className="flex flex-wrap gap-2 text-[10px]">
          {nature.plus && (
            <span className="text-green-400">
              +{STAT_LABELS[nature.plus]}
            </span>
          )}
          {nature.minus && (
            <span className="text-red-400">
              -{STAT_LABELS[nature.minus]}
            </span>
          )}
        </div>
      )}
    </button>
  );
}